/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import {useCallback} from 'react';
import {useDispatch} from 'react-redux';
import moment, {Moment} from 'moment';
import {isCurrentTheme} from '@ion/dls/src/theme/useThemeContext';
import {DateSelectorValues, TimeModes, TrackingMode} from '@ion/dls/src/types/types';
import {
  cancelLiveHistory,
  setHistoryActive,
  setHistoryFrom,
  setHistoryPreset,
  setHistoryTo,
  setTimeMode,
  setTimeModePanelOpen,
  setTrackingMode
} from '../../../../store/actions/tracking';
import {acceptHistoryReport} from '../../../tracking2/map/containers/store';

/**
 * Defines the History range a report reached from outside the Time Mode
 * control is run over.
 */
export interface HistoryTimeModeSelection {
  preset: DateSelectorValues;
  from: Date | Moment;
  to: Date | Moment;
}

/**
 * The useApplyHistoryTimeMode hook moves the current theme's Time Mode
 * control to History for a report reached from outside it - a Vicinity
 * Search over past hours, or View History from an asset's side sheet. The
 * range is kept the way the History panel's own Apply keeps it, so the
 * control, its chip and the filter's limit check all read the report as
 * running. Moving the mode asks for its panel as well, which these reports
 * have no use for - their range is already chosen - so it is put away
 * straight after, and the report is let through the same gate the panel's
 * Apply opens. Any timeline left running is stopped, the two never running
 * together. The legacy theme has no Time Mode control, so nothing is moved
 * there.
 *
 * ```typescript
 *
 *   import useApplyHistoryTimeMode from './hooks/history/useApplyHistoryTimeMode';
 *   ...
 *
 *   const applyHistoryTimeMode = useApplyHistoryTimeMode();
 *
 *   applyHistoryTimeMode({
 *     preset : DateSelectorValues.Custom,
 *     from   : startOfDay(date),
 *     to     : endOfDay(date)
 *   });
 *
 * ```
 *
 * @returns The callback that moves the Time Mode control to History over the
 *          range given.
 */
export const useApplyHistoryTimeMode = () => {
  const dispatch = useDispatch();

  const applyHistory = useCallback((selection: HistoryTimeModeSelection) => {
    applyHistoryTimeMode(dispatch, selection);
  }, [dispatch]);

  return applyHistory;
};

/**
 * Moves the current theme's Time Mode control to History over the range
 * given, through the `dispatch` handed in - the steps `useApplyHistoryTimeMode`
 * takes, for a report reached outside a component, such as a deep link (a
 * notification's View Event) handled by a thunk. Does nothing in the legacy
 * theme.
 *
 * ```typescript
 *
 *   import {applyHistoryTimeMode} from '@/routes/tracking/hooks/history/useApplyHistoryTimeMode';
 *   ...
 *
 *   applyHistoryTimeMode(dispatch, {
 *     preset : DateSelectorValues.Custom,
 *     from   : startOfDay(date),
 *     to     : endOfDay(date)
 *   });
 *
 * ```
 *
 * @param dispatch  The store's dispatch.
 * @param selection The preset and the range the report runs over.
 */
export const applyHistoryTimeMode = (dispatch: Function, {preset, from, to}: HistoryTimeModeSelection) => {
  if (isCurrentTheme()) {
    dispatch(setHistoryPreset(preset));
    dispatch(setHistoryFrom(moment(from).toDate()));
    dispatch(setHistoryTo(moment(to).toDate()));
    dispatch(setHistoryActive(true));
    dispatch(cancelLiveHistory());
    dispatch(setTrackingMode(TrackingMode.History));
    dispatch(setTimeMode(TimeModes.history));
    dispatch(setTimeModePanelOpen(false));
    acceptHistoryReport();
  }
};

export default useApplyHistoryTimeMode;
